"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BILL_COLUMNS = exports.deleteBillSchema = exports.updateBillSchema = exports.billListQuerySchema = exports.BILL_STATUSES = exports.BILL_KINDS = void 0;
exports.billClientName = billClientName;
const zod_1 = require("zod");
/**
 * Bills — SPEC §5.6.
 *
 * Four sub-tabs over one `Bill` table, discriminated by `kind`:
 *
 *   RESERVATION  — one per closed Session (a party of twelve produces twelve)
 *   CASHIER      — a staff-entered cafe order
 *   PORTAL_ORDER — a cafe order the customer placed from the in-PC portal Shop
 *   REFILL       — a customer account top-up (or a balance payout)
 *
 * DATE FILTERING IS ON `businessDate`, NEVER `createdAt` (§4.1). The From/To
 * pickers are *date-time* pickers because that is how a venue reads its own
 * day — "25/7 09:00 -> 26/7 08:00" is the business day of the 25th written out
 * in full. The server resolves each instant to the business date containing it
 * and filters on that, so a 02:00 sale stays with the evening it belongs to
 * instead of jumping to the next report.
 *
 * MONEY OVER THE WIRE: every `*Md` field is millidinars as a decimal STRING.
 * Parse with `fromMillidinars`, never `Number()`.
 */
/* -------------------------------------------------------------------------- */
/* Enums                                                                      */
/* -------------------------------------------------------------------------- */
/** The four sub-tabs, in the order SPEC §5.6 lists them. */
exports.BILL_KINDS = ['RESERVATION', 'CASHIER', 'PORTAL_ORDER', 'REFILL'];
exports.BILL_STATUSES = ['OPEN', 'PAID', 'VOID', 'REFUNDED'];
/* -------------------------------------------------------------------------- */
/* Query                                                                      */
/* -------------------------------------------------------------------------- */
/**
 * An ISO instant from a date-time picker. Resolved server-side to the business
 * date containing it — see the module header for why.
 */
const isoInstant = zod_1.z
    .string()
    .trim()
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Expected an ISO date-time.');
/** SPEC §5.6 filters: search, Device Section, All Users, From/To. */
exports.billListQuerySchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.BILL_KINDS).default('RESERVATION'),
    /** Matches client name (customer OR visitor), device name, or invoice number. */
    search: zod_1.z.string().trim().max(80).optional(),
    /**
     * The number off a customer's receipt (owner, 2026-09-23: "so if a customer
     * comes back to us about it we can search for it on our end"). An exact
     * match, and — unlike every other filter here — NOT bounded by From/To or
     * `kind`: the customer is holding the paper, not the date, and one receipt
     * can carry a reservation and a cafe order together.
     */
    receiptNumber: zod_1.z.string().trim().min(1).max(32).optional(),
    /** "Device Section" — a DeviceCategory id. */
    deviceCategoryId: zod_1.z.string().min(1).optional(),
    /** "All Users" — the issuing employee. Omit for all. */
    employeeId: zod_1.z.string().min(1).optional(),
    /** Defaults to the start of the current business day. */
    from: isoInstant.optional(),
    /** Defaults to the end of the current business day. */
    to: isoInstant.optional(),
    status: zod_1.z.enum(exports.BILL_STATUSES).optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(500).default(50),
});
/* -------------------------------------------------------------------------- */
/* Write models                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Edit a bill (SPEC §5.6 "Edit" — permission-gated and audited).
 *
 * Deliberately narrow. The money a session actually generated is computed
 * server-side from the rate snapshot (§4.1), so an edit may adjust the
 * discount and correct who the bill is for — it may NOT rewrite the subtotal,
 * because that would make the bill disagree with the session behind it.
 * `totalMd` is recomputed from `subtotal - discount` and re-rounded once.
 */
exports.updateBillSchema = zod_1.z
    .object({
    /** New discount in whole IQD. Omit to leave unchanged. */
    discountIqd: zod_1.z.number().int().min(0).max(1_000_000_000).optional(),
    /** Correct a mistyped walk-in name. Null clears it. */
    visitorName: zod_1.z.string().trim().max(80).nullable().optional(),
    /** Move the bill onto a registered account, or off one with null. */
    customerAccountId: zod_1.z.string().min(1).nullable().optional(),
    /** Why — recorded on the bill and in the audit entry. */
    reason: zod_1.z.string().trim().min(1, 'A reason is required.').max(300),
})
    .refine((value) => value.discountIqd !== undefined ||
    value.visitorName !== undefined ||
    value.customerAccountId !== undefined, { message: 'Nothing to change.', path: ['discountIqd'] });
/**
 * Delete a bill (SPEC §5.6 "Delete" — permission-gated and audited).
 *
 * A reason is mandatory: this is the one action that can make a day's takings
 * disappear, and the audit entry is the only control on it (§3).
 */
exports.deleteBillSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1, 'A reason is required.').max(300),
    /**
     * Cash drawer (owner, 2026-10-12): "Was the cash handed back to the
     * customer?" — asked when the bill holds CASH payments and the drawer is in
     * use. true: a reversing CASH payment takes the notes out of the drawer;
     * false: the cash stays in it. Left out on such a bill, the delete is refused
     * `cash_returned_required`; on any other bill it is ignored. No default: an
     * unanswered question must stay distinguishable from "no".
     */
    cashReturned: zod_1.z.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */
/** Which columns each sub-tab renders, in SPEC order. Drives the grid header. */
exports.BILL_COLUMNS = {
    // SPEC §5.6 lists these explicitly and says to keep every one.
    RESERVATION: [
        'invoiceNumber',
        'customer',
        'visitor',
        'deviceNumber',
        'startDate',
        'endDate',
        'reservationDuration',
        'hourlyRate',
        'numberOfRounds',
        'roundPrice',
        'price',
        'discount',
        'total',
        'receiptEditor',
        'details',
        'print',
        'edit',
        'delete',
    ],
    CASHIER: [
        'invoiceNumber',
        'customer',
        'visitor',
        'destination',
        'date',
        'itemCount',
        'items',
        'price',
        'discount',
        'total',
        'receiptEditor',
        'details',
        'print',
        'edit',
        'delete',
    ],
    PORTAL_ORDER: [
        'invoiceNumber',
        'customer',
        'deviceNumber',
        'date',
        'itemCount',
        'items',
        'orderStatus',
        'price',
        'discount',
        'total',
        'receiptEditor',
        'details',
        'print',
        'edit',
        'delete',
    ],
    REFILL: [
        'invoiceNumber',
        'customer',
        'date',
        'refillAmount',
        'rechargeBonus',
        'totalCredited',
        'balanceAfter',
        'refillType',
        'total',
        'receiptEditor',
        'details',
        'print',
        'edit',
        'delete',
    ],
};
/** The name to show a human: registered customer wins, else the walk-in. */
function billClientName(row) {
    return row.customerName ?? row.visitorName ?? null;
}
//# sourceMappingURL=bills.js.map